/**
 * The Casefile tab (docs/ux/casefile.md): the state machine over the run
 * head and its rebuild: no run, loading, error, review, rebuilding, rebuild
 * failed, built. Owns `?item=` and `?doc=`, the selected item (the first
 * blocking item by default), and passes `locked` and `offline` down so a
 * stale tab that learns of completion re-renders locked everywhere at once.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { say } from '@/app/announcer';
import { useSession } from '@/app/session';
import type { DecisionInput, MatterDetail, SortReviewItem } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { ImportDemoRun } from '../../intake/ImportDemoRun';
import { Skeleton } from '@/components/ui/skeleton';
import { pagesText } from './describe';
import { ItemDecision } from './ItemDecision';
import { ItemCards, ItemSheet, ReviewCard, RunCard } from './Phone';
import { ReplayPanel } from './ReplayPanel';
import { ViewerDialog, type ViewerMode } from './ViewerDialog';
import { countsOf, phaseOf, ReviewBar } from './ReviewBar';
import { ReviewQueue } from './ReviewQueue';
import { MachineVersusHumanSummary } from './Summary';
import { rebuilding as isRebuilding, useAddAdditional, useCompleteReview, useDecide, useGateRefresh, useRebuildAgain, useSortRun, useStartReview } from './useSort';

export function CasefileTab({ matterRef, detail, offline }: { matterRef: string; detail: MatterDetail | undefined; offline: boolean }) {
  const phone = useIsPhone();
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const runId = detail?.sort?.runId ?? null;
  const run = useSortRun(matterRef, runId);
  const view = run.data;
  const head = view?.head;
  const phase = head ? phaseOf(head) : null;
  const locked = head?.completedAt ? { by: head.completedByName ?? head.completedBy ?? 'someone', at: head.completedAt } : null;

  const itemParam = params.get('item');
  const docParam = params.get('doc');
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [refused, setRefused] = useState<{ reasons: string[]; at: string } | null>(null);
  const [viewer, setViewer] = useState<ViewerMode | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const refreshGate = useGateRefresh(matterRef, head);

  const items = view?.items ?? [];
  const defaultItemId = useMemo(() => (items.find((i) => i.decision === 'unresolved' || i.decision === 'escalated') ?? items[0])?.itemId ?? null, [items]);
  const selectedId = itemParam && items.some((i) => i.itemId === itemParam) ? itemParam : docParam ? null : phone || phase === 'built' ? null : defaultItemId;
  const selected: SortReviewItem | null = items.find((i) => i.itemId === selectedId) ?? null;
  // Picked from the working manifest first, so a document a saved split created can be changed in its turn.
  const pickedDocument = docParam && view ? (view.working.manifest.documents.find((d) => d.id === docParam) ?? view.machine.manifest.documents.find((d) => d.id === docParam)) ?? null : null;

  function setParam(key: 'item' | 'doc', value: string | null) {
    setParams((p) => { p.delete('item'); p.delete('doc'); if (value) p.set(key, value); return p; }, { replace: true });
  }

  const start = useStartReview(matterRef, runId ?? '');
  const decide = useDecide(matterRef, runId ?? '');
  const addAdditional = useAddAdditional(matterRef, runId ?? '');
  const complete = useCompleteReview(matterRef, runId ?? '', {
    onRefused: (reasons) => setRefused({ reasons, at: new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false }) }),
    onCompleted: () => { setRefused(null); requestAnimationFrame(() => statusRef.current?.focus()); },
  });
  const rebuildAgain = useRebuildAgain(matterRef, runId ?? '');

  // The first decision starts the review for whoever makes it; the server keeps the first reviewer.
  const ensureStarted = () => { if (head && !head.startedAt && !head.completedAt) start.mutate(); };

  // Once the review leaves the review phase in this tab, the summary replaces the open item (docs/ux/casefile.md).
  const lastPhase = useRef<string | null>(null);
  useEffect(() => {
    const was = lastPhase.current;
    lastPhase.current = phase;
    if (was === 'review' && phase !== null && phase !== 'review' && (params.get('item') || params.get('doc'))) {
      setParams((p) => { p.delete('item'); p.delete('doc'); return p; }, { replace: true });
    }
  }, [phase, params, setParams]);

  // The rebuild's outcome, once: the toast, the live region and the header's gate.
  const lastRebuild = useRef<string | null>(null);
  useEffect(() => {
    const state = head?.rebuild?.state ?? null;
    const was = lastRebuild.current;
    lastRebuild.current = state;
    if (!head || state === was) return;
    if ((was === 'queued' || was === 'running') && state === 'succeeded' && head.reviewedPointer) {
      const text = `Reviewed casefile built: ${head.reviewedPointer.documents} documents, ${head.reviewedPointer.billablePages} billable pages. Mark sorted is now available.`;
      toast.success(text);
      say(text);
      refreshGate();
    }
    if ((was === 'queued' || was === 'running') && state === 'failed') {
      toast.error('The reviewed casefile could not be built. Your decisions are kept.', { action: { label: 'Rebuild again', onClick: () => rebuildAgain.mutate() } });
      say('The reviewed casefile could not be built.');
    }
    if (state === 'queued' || state === 'running') say('Rebuilding the reviewed casefile.');
  }, [head, refreshGate, rebuildAgain]);

  if (!detail) return <div className="flex flex-col gap-3"><Skeleton className="h-11 rounded-xl" /><Skeleton className="h-11 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (!detail.sort) {
    return (
      <EmptyState
        title={`No sort run has been imported for ${matterRef}.`}
        description="A run is the sorter's output: run.json, manifest.json, review-queue.json and the sorted PDF. The replay and the review queue appear here once one is imported."
        action={<ImportDemoRun matterRef={matterRef} />}
      />
    );
  }
  if (run.isError && !view) return <ErrorPanel message="The sort run could not be loaded." onRetry={() => void run.refetch()} />;
  if (!view || !head || !phase) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <Skeleton className="h-11 rounded-xl" />
        <Skeleton className="h-11 rounded-xl" />
        <div className="grid gap-6 xl:grid-cols-[400px_minmax(0,1fr)]">
          <div className="flex flex-col gap-px">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 rounded-none first:rounded-t-xl last:rounded-b-xl" />)}</div>
          <Skeleton className="h-[560px] rounded-xl" />
        </div>
      </div>
    );
  }

  const counts = countsOf(items);
  const manifest = view.machine.manifest;
  const working = view.working.manifest;
  // A flagged item is read against the machine's manifest, which is what it is about; an additional item about a document the machine never had reads against the working one.
  const manifestFor = (item: SortReviewItem | null) => (item && item.category === 'additional' && !manifest.documents.some((d) => item.documentIds.includes(d.id)) ? working : manifest);
  const mine = Boolean(me && head.reviewer && (head.reviewer === me.sub || head.reviewer === me.actingAs));

  function onDecide(input: DecisionInput) {
    if (!selected && pickedDocument) {
      if (!input.correction) return;
      ensureStarted();
      addAdditional.mutate({ documentId: pickedDocument.id, correction: input.correction, ...(input.note ? { note: input.note } : {}) }, {
        onSuccess: (item) => {
          setParam('item', item.itemId);
          const remaining = items.filter((i) => i.decision === 'unresolved' || i.decision === 'escalated').length;
          say(`Correction recorded: ${pagesText(item.startPage, item.endPage).toLowerCase()}. ${remaining} ${remaining === 1 ? 'item blocks' : 'items block'} completion.`);
        },
      });
      return;
    }
    if (!selected) return;
    ensureStarted();
    const label = `the decision for ${pagesText(selected.startPage, selected.endPage).toLowerCase()}`;
    decide.mutate({ itemId: selected.itemId, input, label }, {
      onSuccess: (item) => {
        // A refused completion's reasons are stale once a decision changes.
        setRefused(null);
        const remaining = items.filter((i) => i.itemId !== item.itemId && (i.decision === 'unresolved' || i.decision === 'escalated')).length + (item.decision === 'escalated' ? 1 : 0);
        say(`Decision saved: ${pagesText(item.startPage, item.endPage).toLowerCase()} ${item.decision}. ${remaining} ${remaining === 1 ? 'item blocks' : 'items block'} completion.`);
      },
    });
  }

  if (phone) {
    return (
      <div className="flex flex-col gap-3">
        <RunCard matterRef={matterRef} head={head} onView={setViewer} />
        <ReviewCard head={head} counts={counts} phase={phase} machine={manifest} reviewed={view.reviewed} />
        <ItemCards items={items} manifest={manifest} threshold={view.escalationConfidence} onOpen={(id) => { setParam('item', id); setSheetOpen(true); }} />
        <ItemSheet matterRef={matterRef} runId={head.runId} item={selected} manifest={manifest} threshold={view.escalationConfidence} open={sheetOpen && selected !== null} onOpenChange={(o) => { setSheetOpen(o); if (!o) setParam('item', null); }} />
        <ViewerDialog matterRef={matterRef} head={head} view={view} mode={viewer} locked={locked !== null} onClose={() => setViewer(null)} onChangeDocument={(id) => { setViewer(null); setParam('doc', id); }} />
      </div>
    );
  }

  const showSummary = phase !== 'review' && !selected;
  return (
    <div className="flex flex-col gap-3" data-slot="casefile-tab">
      <ReplayPanel head={head} onView={setViewer} />
      <ReviewBar
        matterRef={matterRef}
        head={head}
        counts={counts}
        phase={phase}
        mine={mine}
        myName={me?.actingAsName ?? me?.name ?? null}
        offline={offline}
        phone={false}
        pollStalled={isRebuilding(head) && run.failureCount >= 2}
        onCheckNow={() => void run.refetch()}
        completing={complete.isPending}
        refusedReasons={refused?.reasons ?? null}
        refusedAt={refused?.at ?? null}
        onComplete={async () => { setRefused(null); try { await complete.mutateAsync(); return true; } catch { return false; } }}
        onRebuildAgain={() => rebuildAgain.mutate()}
        rebuilding={rebuildAgain.isPending || isRebuilding(head)}
        statusRef={statusRef}
      />
      <div className="mt-1 grid items-start gap-6 lg:grid-cols-[360px_minmax(0,1fr)] xl:grid-cols-[400px_minmax(0,1fr)]">
        <ReviewQueue
          matterRef={matterRef}
          items={items}
          manifest={manifest}
          working={working}
          escalationConfidence={view.escalationConfidence}
          selectedId={selectedId}
          blockingOnly={blockingOnly}
          locked={locked !== null}
          onSelect={(id) => setParam('item', id)}
          onOpen={() => headingRef.current?.focus()}
          onToggleBlocking={() => setBlockingOnly((v) => !v)}
          onPickDocument={(docId) => setParam('doc', docId)}
          pickedDocumentId={docParam}
        />
        {showSummary ? (
          <MachineVersusHumanSummary matterRef={matterRef} head={head} items={items} machine={manifest} reviewed={view.reviewed} matterStatus={detail.matter.status} onOpenItem={(id) => setParam('item', id)} />
        ) : selected || pickedDocument ? (
          <ItemDecision
            matterRef={matterRef}
            runId={head.runId}
            item={selected}
            pickedDocument={selected ? null : pickedDocument}
            manifest={selected ? manifestFor(selected) : working}
            escalationConfidence={view.escalationConfidence}
            locked={locked}
            offline={offline}
            busy={decide.isPending || addAdditional.isPending}
            // Keyed to the item the failed save was for, so the next item does not open reading "Not saved: try again."
            saveFailed={(decide.isError && decide.variables?.itemId === selected?.itemId) || (addAdditional.isError && selected === null)}
            onDecide={onDecide}
            onWithdraw={() => { if (selected) decide.mutate({ itemId: selected.itemId, input: { decision: 'accepted' }, label: 'the withdrawal' }); }}
            onBack={phase !== 'review' ? () => setParam('item', null) : undefined}
            headingRef={headingRef}
          />
        ) : (
          <EmptyState title="Nothing to decide." description="Complete the review to build the reviewed casefile and open the 3 to 4 gate." />
        )}
      </div>
      <p className="text-[13px] text-beam-text-muted">Order within a section follows the document date; to move a document, correct its date. Manual reordering is not offered.</p>
      <ViewerDialog matterRef={matterRef} head={head} view={view} mode={viewer} locked={locked !== null} onClose={() => setViewer(null)} onChangeDocument={(id) => { setViewer(null); setParam('doc', id); }} />
    </div>
  );
}
